import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type Dispatch,
  type ReactElement,
  type SetStateAction,
} from 'react';
import toast from 'react-hot-toast';
import { IconPlus, IconRefresh, IconX } from '@tabler/icons-react';
import { Button } from '../../Button/Button';

import { useFetchCallback } from '@services/services';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';
import type { Category, Extra, Product, Section } from '@interfaces/general';
import type {
  DetectedCategory,
  DetectedExtra,
  DetectedProduct,
  DetectedSection,
  ProductPresentation,
} from '@pages/Menu/Products/MenuUploaderV2/types/menuUpload.types';

import { V4 } from '../../tokens';
import { ACCEPTED_PDF_MIME, MAX_FILE_MB, MAX_IMAGES, MAX_PDFS, MAX_TOTAL_MB } from './constants';
import { ConsolidatedReview } from './components/ConsolidatedReview';
import { DuplicateBanner } from './components/DuplicateBanner';
import { FileQueueGrid } from './components/FileQueueGrid';
import { LowExtractionPrompt } from './components/LowExtractionPrompt';
import { MultiFileUploadZone } from './components/MultiFileUploadZone';
import { ProcessingProgress } from './components/ProcessingProgress';
import { RecoveryBanner } from './components/RecoveryBanner';
import { SkipFooter } from './components/SkipFooter';
import { useConsolidation } from './hooks/useConsolidation';
import { useMultiFileMenuProcessing } from './hooks/useMultiFileMenuProcessing';
import type {
  ConsolidatedAnalysis,
  DuplicateGroup,
  SmartMenuCompletionResult,
  SmartMenuOnboardingProps,
  UploadQueueItem,
} from './types';
import { getCooldown, markAttempt } from './utils/cooldown';
import { remapDuplicateDecisions } from './utils/duplicateDecisions';
import { loadPendingJob, clearPendingJob, type PendingJob } from './utils/pendingJob';
import { loadReviewDraft, saveReviewDraft, clearReviewDraft } from './utils/reviewDraft';
import { buildExistingCategoryLinks, findExistingProductDuplicates } from './utils/existingCatalogMatch';
import { buildSyntheticCategory } from './utils/syntheticCategory';

type Phase = 'upload' | 'processing' | 'low-extraction' | 'review' | 'persisting' | 'done';

type DuplicateDecisionMap = Record<number, 'keep_representative' | 'keep_all'>;

const LOW_EXTRACTION_THRESHOLD = 3;
const DONE_DELAY_MS = 500;

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  width: '100%',
};

const ctaWrapperStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  marginTop: 8,
};

const spinnerOverlayStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 48,
  gap: 16,
};

const spinnerCircleStyle: CSSProperties = {
  width: 36,
  height: 36,
  border: `3px solid ${V4.grayLight}`,
  borderTopColor: V4.secondary,
  borderRadius: '50%',
  animation: 'sm-onboarding-spin 1s linear infinite',
};

const doneCardStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: 32,
  gap: 12,
  color: V4.primary,
};

// Banner informativo (append en curso): misma anatomía que el batch-error pero
// en tono informativo — el usuario puede seguir editando mientras analiza.
const infoBannerStyle: CSSProperties = {
  padding: '12px 16px',
  background: '#F0F9FC',
  border: `1px solid ${V4.secondary}`,
  borderRadius: 8,
  color: V4.primary,
  fontSize: 13,
  lineHeight: 1.5,
};

const errorBannerStyle: CSSProperties = {
  padding: '12px 16px',
  background: '#FEF2F2',
  border: `1px solid ${V4.danger}`,
  borderRadius: 8,
  color: V4.danger,
  fontSize: 13,
  lineHeight: 1.5,
};

const linkButtonStyle: CSSProperties = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  color: V4.secondary,
  fontSize: 13,
  fontWeight: 600,
  textDecoration: 'underline',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const appendSheetBackdropStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(17, 26, 34, 0.55)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
};

const appendSheetContainerStyle: CSSProperties = {
  background: V4.white,
  width: '100%',
  maxWidth: 560,
  maxHeight: '85vh',
  borderTopLeftRadius: 16,
  borderTopRightRadius: 16,
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  boxShadow: '0 -8px 24px rgba(17, 26, 34, 0.18)',
  overflowY: 'auto',
};

interface AppendBannersProps {
  inFlightCount: number;
  inFlightFiles: number;
  failedAppends: UploadQueueItem[];
  onRetry: (id: string) => void;
  onDiscard: (id: string) => void;
}

/**
 * Banners de estado de los batches ADITIVOS en review: progreso informativo
 * (el usuario sigue editando mientras analiza) y errores por batch con
 * Reintentar/Descartar que nunca rompen la curaduría existente (spec §6.1).
 *
 * @param {AppendBannersProps} props Estado derivado de los appends + handlers.
 * @returns {ReactElement} Fragment con 0..N banners.
 */
const AppendBanners = ({
  inFlightCount,
  inFlightFiles,
  failedAppends,
  onRetry,
  onDiscard,
}: AppendBannersProps): ReactElement => (
  <>
    {inFlightCount > 0 ? (
      <div data-testid="smart-menu-onboarding-append-progress" role="status" style={infoBannerStyle}>
        <strong style={{ display: 'block', marginBottom: 4 }}>
          {`Analizando ${inFlightFiles} archivo${inFlightFiles === 1 ? '' : 's'} nuevo${inFlightFiles === 1 ? '' : 's'}…`}
        </strong>
        Puedes seguir revisando y editando tu menú mientras tanto.
      </div>
    ) : null}
    {failedAppends.map((item) => (
      <div key={item.id} data-testid="smart-menu-onboarding-append-error" role="alert" style={errorBannerStyle}>
        <strong style={{ display: 'block', marginBottom: 4 }}>No pudimos procesar los archivos nuevos</strong>
        <span style={{ display: 'block', marginBottom: 8 }}>
          {item.error?.message ?? 'Ocurrió un error al analizar los archivos.'}
        </span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            data-testid="smart-menu-onboarding-append-retry"
            variant="primary"
            size="sm"
            onClick={() => onRetry(item.id)}
          >
            Reintentar
          </Button>
          <Button
            data-testid="smart-menu-onboarding-append-discard"
            variant="ghost"
            size="sm"
            onClick={() => onDiscard(item.id)}
          >
            Descartar archivos nuevos
          </Button>
        </div>
      </div>
    ))}
  </>
);

interface PublishErrorBannerProps {
  error: { failedProducts: number; totalProducts: number; message: string };
  onRetry: () => void;
  onRestart: () => void;
}

/**
 * Banner persistente de fallo parcial al publicar (spec §6.4): reemplaza al
 * toast efímero. REINTENTAR continúa donde quedó (idempotencia del caller).
 *
 * @param {PublishErrorBannerProps} props Conteos del fallo + handlers.
 * @returns {ReactElement} Banner inline con acciones.
 */
const PublishErrorBanner = ({ error, onRetry, onRestart }: PublishErrorBannerProps): ReactElement => (
  <div data-testid="smart-menu-onboarding-publish-error" role="alert" style={errorBannerStyle}>
    <strong style={{ display: 'block', marginBottom: 4 }}>
      {`No pudimos crear ${error.failedProducts} de ${error.totalProducts} producto${error.totalProducts === 1 ? '' : 's'}.`}
    </strong>
    <span style={{ display: 'block', marginBottom: 8 }}>{error.message}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <Button data-testid="smart-menu-onboarding-publish-retry" variant="primary" size="sm" onClick={onRetry}>
        Reintentar
      </Button>
      <button
        type="button"
        data-testid="smart-menu-onboarding-publish-error-restart"
        style={linkButtonStyle}
        onClick={onRestart}
      >
        Volver a empezar
      </button>
    </div>
  </div>
);

interface AppendSheetProps {
  usage: { pdf: number; image: number; totalMB: number };
  displayItems: UploadQueueItem[];
  fileCount: number;
  maxPdfs: number;
  maxImages: number;
  maxFileMB: number;
  maxTotalMB: number;
  onFilesAdded: (files: File[]) => void;
  onRemoveFile: (id: string) => void;
  onClose: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Bottom sheet de "+ Agregar fotos o PDF" en review: MultiFileUploadZone con
 * límites RESTANTES (los conteos en `usage` incluyen lo ya procesado) y
 * confirmación que lanza el batch aditivo.
 *
 * @param {AppendSheetProps} props Uso acumulado, selección actual y handlers.
 * @returns {ReactElement} Sheet modal sobre el review.
 */
const AppendSheet = ({
  usage,
  displayItems,
  fileCount,
  maxPdfs,
  maxImages,
  maxFileMB,
  maxTotalMB,
  onFilesAdded,
  onRemoveFile,
  onClose,
  onCancel,
  onConfirm,
}: AppendSheetProps): ReactElement => (
  <div
    data-testid="smart-menu-onboarding-append-sheet"
    role="dialog"
    aria-modal="true"
    aria-label="Agregar fotos o PDF"
    style={appendSheetBackdropStyle}
    onClick={onClose}
  >
    <div style={appendSheetContainerStyle} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <div style={{ color: V4.primary, fontSize: 16, fontWeight: 700 }}>Agregar fotos o PDF</div>
          <div style={{ color: V4.grayUltraDark, fontSize: 12, marginTop: 4 }}>
            Los productos nuevos se sumarán a tu revisión sin perder tus cambios.
          </div>
        </div>
        <button
          type="button"
          aria-label="Cerrar"
          data-testid="smart-menu-onboarding-append-close"
          style={{
            background: 'transparent',
            border: 'none',
            padding: 4,
            cursor: 'pointer',
            color: V4.grayUltraDark,
            display: 'inline-flex',
          }}
          onClick={onClose}
        >
          <IconX size={20} />
        </button>
      </div>
      <MultiFileUploadZone
        onFilesAdded={onFilesAdded}
        currentPdfCount={usage.pdf}
        currentImageCount={usage.image}
        currentTotalMB={usage.totalMB}
        maxPdfs={maxPdfs}
        maxImages={maxImages}
        maxFileMB={maxFileMB}
        maxTotalMB={maxTotalMB}
      />
      {displayItems.length > 0 ? (
        <FileQueueGrid items={displayItems} onRemove={onRemoveFile} onRetry={() => undefined} />
      ) : null}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="ghost" data-testid="smart-menu-onboarding-append-cancel" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          variant="primary"
          data-testid="smart-menu-onboarding-append-confirm"
          disabled={fileCount === 0}
          onClick={onConfirm}
        >
          {`Analizar ${fileCount > 0 ? `${fileCount} ` : ''}archivo${fileCount === 1 ? '' : 's'}`}
        </Button>
      </div>
    </div>
  </div>
);

const buildIdSet = (ids: string[]): Set<string> => new Set(ids);

/**
 * Selección + auto-incluidos: agrega del universo las entidades requeridas por
 * `neededIds` que el usuario no seleccionó (blindaje anti drop-silencioso).
 *
 * @param {Array<object>} selected Entidades seleccionadas explícitamente.
 * @param {Array<object>} universe Todas las entidades del análisis filtrado.
 * @param {Set<string>} neededIds Ids requeridos por otras entidades seleccionadas.
 * @returns {Array<object>} Seleccionadas + auto-incluidas (sin duplicados).
 */
const withAutoIncluded = <T extends { id: string }>(selected: T[], universe: T[], neededIds: Set<string>): T[] => {
  const selectedIds = new Set(selected.map((s) => s.id));
  const auto = universe.filter((u) => neededIds.has(u.id) && !selectedIds.has(u.id));
  return [...selected, ...auto];
};

/**
 * Payload de opciones del producto a publicar. Con más de una presentación
 * detectada/editada (3 pzas / 6 pzas) se publican TODAS literales; con una o
 * ninguna, el fallback retrocompat V3: presentación única con el precio plano.
 *
 * @param {ProductPresentation[]} presentaciones Presentaciones del análisis (o editadas).
 * @param {number} precioPlano Precio principal (con edición aplicada) para el fallback.
 * @returns {Array<object>} Opciones listas para POST /v1/producto.
 */
/**
 * Vincula los modificadores publicados a sus productos (`id_secciones`) con un
 * PUT por producto. Idempotente en retry (mismo array) y tolerante a productos
 * deseleccionados/no creados (se saltan).
 *
 * @param {DetectedSection[]} selectedSections Modificadores publicados.
 * @param {Map} oldToNewSectionId Mapa oldSectionId → ID de BD.
 * @param {Map} createdProductsByOldId Mapa oldProductId → Product creado.
 * @param {Function} updateProduct PUT /v1/producto (call de useFetchCallback).
 * @returns {Promise} Resuelve al terminar todos los PUT de binding.
 */
/**
 * WS-B (R2): saca de la memoria de idempotencia (`oldToNewCategoryId`/`linkedCategoryIds`)
 * las categorías cuyo link quedó stale entre intentos del publish — desvinculadas por el
 * usuario o con target ya inválido (existente inactiva/borrada) — para que el loop las
 * re-evalúe. Muta los sets/maps recibidos.
 *
 * @param {Set<string>} linkedCategoryIds Ids detectados linkeados en intentos previos.
 * @param {Map<string, string>} oldToNewCategoryId Mapa detectedId → backendId.
 * @param {Record<string, string>} existingCategoryLinks Links vigentes (detectedId → existingId).
 * @param {Set<string>} activeExistingIds Ids de categorías existentes ACTIVAS.
 * @returns {void}
 */
const reconcileStaleCategoryLinks = (
  linkedCategoryIds: Set<string>,
  oldToNewCategoryId: Map<string, string>,
  existingCategoryLinks: Record<string, string>,
  activeExistingIds: Set<string>,
): void => {
  for (const detectedCatId of [...linkedCategoryIds]) {
    const link = existingCategoryLinks[detectedCatId];
    if (!link || !activeExistingIds.has(link)) {
      oldToNewCategoryId.delete(detectedCatId);
      linkedCategoryIds.delete(detectedCatId);
    }
  }
};

const bindSectionsToProducts = async (
  selectedSections: DetectedSection[],
  oldToNewSectionId: Map<string, string>,
  createdProductsByOldId: Map<string, Product>,
  updateProduct: (args: { path?: string; data: Product }) => Promise<unknown>,
): Promise<void> => {
  const sectionDbIdsByProduct = new Map<string, string[]>();
  for (const sec of selectedSections) {
    const newSectionId = oldToNewSectionId.get(sec.id);
    if (!newSectionId) continue;
    for (const oldProdId of sec.applicableToProducts ?? []) {
      const list = sectionDbIdsByProduct.get(oldProdId) ?? [];
      if (!list.includes(newSectionId)) list.push(newSectionId);
      sectionDbIdsByProduct.set(oldProdId, list);
    }
  }
  for (const [oldProdId, sectionDbIds] of sectionDbIdsByProduct) {
    const createdProduct = createdProductsByOldId.get(oldProdId);
    if (!createdProduct) continue; // producto deseleccionado/no creado en este intento
    // `path` con el id de BD → PUT /v1/producto/{id} (handler `update`). Sin `path`
    // pegaba en PUT /v1/producto (handler `updateOrdering`, schema `ordenar` que exige
    // `items`) → 400 INVALID_SCHEMA al ligar modificadores. El id viene del POST previo.
    await updateProduct({ path: createdProduct.id, data: { ...createdProduct, id_secciones: sectionDbIds } });
  }
};

const buildProductOpcionesPayload = (
  presentaciones: ProductPresentation[],
  precioPlano: number,
): NonNullable<Product['opciones']> => {
  if (presentaciones.length > 1) {
    return presentaciones.map((op) => ({
      id: '',
      estatus: 'activo' as const,
      presentacion: op.presentacion || 'pza',
      precio: op.precio,
      costo_produccion: typeof op.costo_produccion === 'number' ? op.costo_produccion : 0,
    }));
  }
  return [
    {
      id: '',
      estatus: 'activo' as const,
      presentacion: presentaciones[0]?.presentacion || 'pza',
      precio: precioPlano,
      costo_produccion: 0,
    },
  ];
};

/**
 * Campos del producto a publicar con las ediciones aplicadas: nombre final y
 * payload de opciones (presentaciones literales o fallback plano, ver
 * `buildProductOpcionesPayload`).
 *
 * @param {DetectedProduct} prod Producto del análisis (ya enriquecido).
 * @param {object} [edit] Patch de edición del usuario (nombre/precio/opciones).
 * @returns {object} `{ nombre, opcionesPayload }` listos para POST /v1/producto.
 */
const buildProductCreateFields = (
  prod: DetectedProduct,
  edit?: { nombre?: string; precio?: number; opciones?: ProductPresentation[] },
): { nombre: string; opcionesPayload: NonNullable<Product['opciones']> } => {
  const nombre = edit?.nombre ?? prod.name;
  const precio = typeof edit?.precio === 'number' ? edit.precio : (prod.price ?? 0);
  // Presentaciones LITERALES (ej. "3 pzas $100 / 6 pzas $150"): si el análisis
  // (o una edición) trae más de una, se publican todas.
  const opcionesPayload = buildProductOpcionesPayload(edit?.opciones ?? prod.opciones ?? [], precio);
  return { nombre, opcionesPayload };
};

const sumPdfImagesSize = (files: File[]): { pdf: number; image: number; totalMB: number } => {
  let pdf = 0;
  let image = 0;
  let totalBytes = 0;
  files.forEach((file) => {
    if (file.type === ACCEPTED_PDF_MIME) pdf += 1;
    else image += 1;
    totalBytes += file.size;
  });
  return { pdf, image, totalMB: totalBytes / (1024 * 1024) };
};

const applyDuplicateDecisionsToAnalysis = (
  analysis: ConsolidatedAnalysis,
  decisions: DuplicateDecisionMap,
): ConsolidatedAnalysis => {
  // `consolidate` ya quitó los candidates de `categories/products/extras`. Para
  // soportar `keep_all` correctamente (Seer bug 3378651276) necesitamos
  // re-introducirlos desde los snapshots pre-dedupe; el filter por sí solo
  // nunca encuentra el candidate (porque ya no está en analysis).
  const idsToRemove = new Set<string>();
  const candidatesToReintroduce: { type: 'category' | 'product' | 'extra'; id: string }[] = [];
  analysis.duplicateGroups.forEach((group, index) => {
    // 'pending' del grupo se trata como 'keep_representative' (default seguro).
    const decision = decisions[index] ?? (group.decision === 'keep_all' ? 'keep_all' : 'keep_representative');
    if (decision === 'keep_all') {
      group.candidateIds.forEach((id) => candidatesToReintroduce.push({ type: group.type, id }));
    } else {
      group.candidateIds.forEach((id) => idsToRemove.add(id));
    }
  });
  if (idsToRemove.size === 0 && candidatesToReintroduce.length === 0) return analysis;

  const extraCategories: DetectedCategory[] = [];
  const extraProducts: DetectedProduct[] = [];
  const extraExtras: DetectedExtra[] = [];
  for (const { type, id } of candidatesToReintroduce) {
    if (type === 'category') {
      const c = analysis.preDedupeCategoriesById[id];
      if (c) extraCategories.push(c);
    } else if (type === 'product') {
      const p = analysis.preDedupeProductsById[id];
      if (p) extraProducts.push(p);
    } else {
      const e = analysis.preDedupeExtrasById[id];
      if (e) extraExtras.push(e);
    }
  }

  return {
    ...analysis,
    categories: [...analysis.categories.filter((c) => !idsToRemove.has(c.id)), ...extraCategories],
    products: [...analysis.products.filter((p) => !idsToRemove.has(p.id)), ...extraProducts],
    extras: [...analysis.extras.filter((e) => !idsToRemove.has(e.id)), ...extraExtras],
  };
};

export const SmartMenuOnboarding = ({
  skippable = true,
  onComplete,
  // onSkip queda deprecated en el API (Seer comment 3372844752); el componente
  // siempre notifica salida via onComplete con result.skipped=true. Se omite del
  // destructure para que ESLint marque cualquier uso interno residual.
  maxPdfs = MAX_PDFS,
  maxImages = MAX_IMAGES,
  maxFileMB = MAX_FILE_MB,
  maxTotalMB = MAX_TOTAL_MB,
  trackingSource = 'cargar_menu',
  // WS-B: catálogo EXISTENTE del comercio para el matching client-side (solo se
  // pasa en la reentrada con catálogo; vacío en onboarding). Empty → el flujo es
  // idéntico al actual (crea todo).
  existingCategories = [],
  existingProducts = [],
}: SmartMenuOnboardingProps): ReactElement => {
  const {
    items,
    totalProgress,
    isProcessing,
    enqueue,
    appendBatch,
    remove,
    retry,
    discardBatch,
    abortAll,
    reset,
    resume,
  } = useMultiFileMenuProcessing();
  // Single-call refactor: el hook procesa N archivos en UNA llamada y expone
  // 0 o 1 items (el batch). Mantenemos aparte `selectedFiles` para que el grid
  // de upload phase muestre todos los archivos seleccionados y permita remover
  // alguno antes de procesar (esa edición no toca al hook, sólo este estado).
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  // Job persistido de una sesión previa (la página se recargó durante el
  // análisis). Si existe, la fase upload muestra el RecoveryBanner.
  const [pendingJob, setPendingJob] = useState<PendingJob | null>(() => loadPendingJob());
  // Previews por nombre de archivo para batches retomados (sin File local):
  // mirroredItems los inyecta como `previewUrl` y el thumbnail usa la URL.
  const [resumedPreviews, setResumedPreviews] = useState<Record<string, string>>({});
  // Último pending retomado: "Reintentar" tras un fallo de resume re-poll-ea el
  // jobId (no hay Files que re-subir, así que retry() del hook sería no-op).
  const lastResumedRef = useRef<PendingJob | null>(null);
  // jobId del análisis activo en review: clave del draft de curaduría.
  const activeJobIdRef = useRef<string | null>(null);
  const baseAnalysis = useConsolidation(items);
  // V4: las sections viven en `item.sections` (prefijadas por fileId desde el hook).
  // `consolidate` no las conoce (sólo cat/prod/extras), así que las agregamos aquí
  // a partir de los items completados. Sin dedup cross-archivo: si el usuario subió
  // dos menús que declaran la misma section "Salsas", verá ambas y puede deseleccionar
  // la duplicada. Es preferible a un dedupe heurístico que mezcle opciones[] distintas.
  //
  // Remap obligatorio de `section.opciones`: los extras SÍ se deduplican en
  // `consolidate` y los IDs originales desaparecen del `extras[]` kept (quedan
  // mapeados al representante via `extrasRemap`). Si no aplicamos el remap aquí,
  // las sections quedan apuntando a IDs colgados y la UI muestra "no encontrado"
  // (orphan badge). Ver `ConsolidatedReview.tsx:788` (extrasById.get).
  const analysis = useMemo(() => {
    const aggregatedSections: DetectedSection[] = [];
    const extrasRemap = baseAnalysis.extrasRemap;
    const hasRemap = Object.keys(extrasRemap).length > 0;
    for (const item of items) {
      if (item.status !== 'completed' || !item.sections) continue;
      for (const sec of item.sections) {
        // Si no hay remap (V3 o un solo archivo sin duplicados de extras) evitamos
        // el spread innecesario para no romper igualdad referencial entre renders.
        if (!hasRemap) {
          aggregatedSections.push(sec);
          continue;
        }
        const remappedOpciones = sec.opciones.map((extId) => extrasRemap[extId] ?? extId);
        // De-dupe local: si dos opciones del mismo section colapsaron al mismo
        // representante, evitamos repetir el extra en la lista.
        const dedupedOpciones: string[] = [];
        const seen = new Set<string>();
        for (const id of remappedOpciones) {
          if (seen.has(id)) continue;
          seen.add(id);
          dedupedOpciones.push(id);
        }
        aggregatedSections.push({ ...sec, opciones: dedupedOpciones });
      }
    }
    if (aggregatedSections.length === baseAnalysis.sections.length && aggregatedSections.length === 0) {
      return baseAnalysis;
    }
    return { ...baseAnalysis, sections: aggregatedSections };
  }, [baseAnalysis, items]);
  const { trackEvent } = useTracking();

  const [phase, setPhase] = useState<Phase>('upload');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
  const [selectedExtraIds, setSelectedExtraIds] = useState<Set<string>>(new Set());
  const [selectedSectionIds, setSelectedSectionIds] = useState<Set<string>>(new Set());
  const [duplicateDecisions, setDuplicateDecisions] = useState<DuplicateDecisionMap>({});
  const [editedCategoryNames, setEditedCategoryNames] = useState<Record<string, string>>({});
  const [editedProductPatches, setEditedProductPatches] = useState<
    Record<string, { nombre?: string; precio?: number; opciones?: ProductPresentation[] }>
  >({});
  const [editedExtraPatches, setEditedExtraPatches] = useState<Record<string, { nombre?: string; precio?: number }>>(
    {},
  );
  const [editedSectionNames, setEditedSectionNames] = useState<Record<string, string>>({});
  const [startTime, setStartTime] = useState<number>(() => Date.now());
  // Reasignaciones de categoría por producto (EditSheet select + bulk de huérfanos).
  const [categoryAssignments, setCategoryAssignments] = useState<Record<string, string>>({});
  // Categorías sintéticas creadas client-side ("+ Crear categoría nueva…").
  const [syntheticCategories, setSyntheticCategories] = useState<DetectedCategory[]>([]);
  // Ledger de batches ADITIVOS lanzados desde review (batchId → files): alimenta
  // los límites restantes del sheet "+ Agregar" y el descarte de un batch fallido.
  const [appendedBatches, setAppendedBatches] = useState<Record<string, File[]>>({});
  const [appendSheetOpen, setAppendSheetOpen] = useState<boolean>(false);
  const [appendSheetFiles, setAppendSheetFiles] = useState<File[]>([]);
  // Fallo parcial al publicar (spec §6.4): banner persistente sobre la barra de
  // publicar con REINTENTAR idempotente. null = sin error visible.
  const [publishError, setPublishError] = useState<{
    failedProducts: number;
    totalProducts: number;
    message: string;
  } | null>(null);

  const startedRef = useRef<boolean>(false);
  const transitionedRef = useRef<boolean>(false);
  const completedRef = useRef<boolean>(false);
  // Items (batches) cuyas entidades YA se integraron a la selección. Permite que
  // un batch aditivo completado durante review sume sus ids nuevos SIN resetear
  // la curaduría existente (spec §6.1).
  const integratedItemIdsRef = useRef<Set<string>>(new Set());
  // Snapshot de duplicateGroups del último análisis integrado: las decisiones
  // van keyed por índice y un append puede desplazar índices — se re-mapean por
  // identidad estable (type + representativeId).
  const lastDuplicateGroupsRef = useRef<DuplicateGroup[]>([]);
  // Anti re-entrancy de handlePersist: previene double-click (sincronico, no espera setPhase).
  const persistingRef = useRef<boolean>(false);
  // Idempotencia entre intentos: si handlePersist falla a mitad, el siguiente click
  // continua donde quedo en lugar de duplicar entidades ya creadas en el backend.
  // Se limpia en exito completo y en handleRestart.
  const createdEntitiesRef = useRef<{
    categoryMap: Map<string, string>; // oldId -> newDbId (sobrevive entre intentos)
    productIds: Set<string>; // ids ya persistidos
    /** oldId -> Product creado (respuesta del POST): alimenta el binding id_secciones. */
    productMap: Map<string, Product>;
    extraIds: Set<string>;
    /** oldExtraId -> newDbId: traduce las opciones de los modificadores al publicar. */
    extraMap: Map<string, string>;
    /**
     * V4: oldSectionId -> newDbId. Sobrevive entre intentos para idempotencia
     * del 4° loop (sections). El Set `sectionIds` redunda con el Map para
     * mantener simetría con productIds/extraIds en el resto del flujo.
     */
    sectionMap: Map<string, string>;
    sectionIds: Set<string>;
    /**
     * WS-B: ids de categorías DETECTADAS que se asociaron a una existente (se
     * reusó su id en `categoryMap` sin POST). Trackearlo aquí lo hace retry-safe
     * y permite reportar `categoriesLinked` aparte de `categoriesCreated` (R1).
     */
    linkedCategoryIds: Set<string>;
  }>({
    categoryMap: new Map(),
    productIds: new Set(),
    productMap: new Map(),
    extraIds: new Set(),
    extraMap: new Map(),
    sectionMap: new Map(),
    sectionIds: new Set(),
    linkedCategoryIds: new Set(),
  });
  // setTimeout id del onComplete diferido (DONE_DELAY_MS). Cancelable en restart/unmount
  // para evitar que un onComplete stale dispare con datos del intento anterior si el
  // usuario presiona "Empezar de cero" dentro de los 500ms (Seer comment 3375274913).
  const completionTimeoutRef = useRef<number | null>(null);
  // Counts reales persistidos (no los detectados). Se mostraran en la pantalla 'done'
  // para reflejar lo que el usuario realmente publico, no lo que la IA detecto
  // (Seer comment 3375387525).
  const [persistedCounts, setPersistedCounts] = useState<{
    categories: number;
    categoriesLinked: number;
    products: number;
    extras: number;
    sections: number;
  }>({ categories: 0, categoriesLinked: 0, products: 0, extras: 0, sections: 0 });

  const { call: createCategoryCall } = useFetchCallback<Category, Category>('post', '/v1/categoria');
  const { call: createProductCall } = useFetchCallback<Product, Product>('post', '/v1/producto');
  const { call: createExtraCall } = useFetchCallback<Extra, Extra>('post', '/v1/extra');
  const { call: createSectionCall } = useFetchCallback<Section, Section>('post', '/v1/seccion');
  // Binding post-creación: el POST de producto ocurre ANTES de que existan los
  // IDs de sección en BD — id_secciones se completa con un PUT por producto.
  const { call: updateProductCall } = useFetchCallback<Product, Product>('put', '/v1/producto');

  // Capa de curaduría encima del análisis consolidado: categorías sintéticas
  // (creadas client-side) + reasignaciones de categoría por producto. Es la
  // fuente que ven el review y el persist.
  const enrichedAnalysis = useMemo(() => {
    // Capa de curaduría COMPLETA para display y persist: categorías sintéticas,
    // reasignaciones Y ediciones (nombres/precios/presentaciones). Sin aplicar
    // las ediciones aquí, la lista seguía mostrando los valores originales
    // después de Guardar en el editor.
    const categories = [
      ...analysis.categories.map((c) => {
        const editedName = editedCategoryNames[c.id];
        return editedName ? { ...c, name: editedName } : c;
      }),
      ...syntheticCategories,
    ];
    const products = analysis.products.map((p) => {
      const target = categoryAssignments[p.id];
      const edit = editedProductPatches[p.id];
      if (!target && !edit) return p;
      const opciones = edit?.opciones ?? p.opciones;
      return {
        ...p,
        category: target ?? p.category,
        name: edit?.nombre ?? p.name,
        // El precio principal sigue a la primera presentación editada, o al
        // precio plano editado cuando no hay multipresentación.
        price: edit?.opciones?.[0]?.precio ?? edit?.precio ?? p.price,
        opciones,
      };
    });
    const extras = analysis.extras.map((e) => {
      const edit = editedExtraPatches[e.id];
      if (!edit) return e;
      return { ...e, name: edit.nombre ?? e.name, price: edit.precio ?? e.price };
    });
    const sections = analysis.sections.map((s) => {
      const editedName = editedSectionNames[s.id];
      return editedName ? { ...s, name: editedName } : s;
    });
    return { ...analysis, categories, products, extras, sections };
  }, [
    analysis,
    syntheticCategories,
    categoryAssignments,
    editedProductPatches,
    editedCategoryNames,
    editedExtraPatches,
    editedSectionNames,
  ]);

  // Orden: tal como se procesó el menú (decisión de producto 2026-06-11 — se
  // eliminó el reordenamiento manual y el orden de vista). El persist itera
  // estos arrays y manda `orden` explícito por categoría, así que el orden de
  // detección ES el orden publicado.
  const viewAnalysis = enrichedAnalysis;

  // ── WS-B: matching contra el catálogo EXISTENTE (overlay; NO toca consolidate) ──
  // `linkOverrides` captura las decisiones del usuario en el review:
  //   string = asociar manual a esa categoría existente · null = desvincular.
  const [linkOverrides, setLinkOverrides] = useState<Record<string, string | null>>({});
  // Asociaciones automáticas categoría detectada → existente (match exacto
  // normalizado; el matcher filtra inactivas). Recomputa si cambia el análisis o
  // el catálogo — por eso un `keep_all` que re-introduce candidatas las evalúa (R6).
  const autoCategoryLinks = useMemo(
    () => buildExistingCategoryLinks(viewAnalysis.categories, existingCategories),
    [viewAnalysis.categories, existingCategories],
  );
  // Efectivo = auto + overrides (null desvincula). Lo consumen el chip del review
  // y el seam del publish.
  const existingCategoryLinks = useMemo(() => {
    const merged: Record<string, string> = { ...autoCategoryLinks };
    for (const [detectedId, target] of Object.entries(linkOverrides)) {
      if (target === null) delete merged[detectedId];
      else merged[detectedId] = target;
    }
    return merged;
  }, [autoCategoryLinks, linkOverrides]);
  // Categorías existentes ACTIVAS indexadas (chip "se agregará a [nombre]" + dropdown del editor).
  const existingCategoriesById = useMemo(() => {
    const map: Record<string, Category> = {};
    for (const c of existingCategories) if (c.estatus === 'activo') map[c.id] = c;
    return map;
  }, [existingCategories]);
  // Productos existentes agrupados por categoría (aviso de duplicados, acotado a la categoría asociada).
  const existingProductsByCategory = useMemo(() => {
    const map = new Map<string, Product[]>();
    for (const p of existingProducts) {
      const arr = map.get(p.id_categoria) ?? [];
      arr.push(p);
      map.set(p.id_categoria, arr);
    }
    return map;
  }, [existingProducts]);
  const productDuplicateHints = useMemo(
    () => findExistingProductDuplicates(viewAnalysis.products, existingProductsByCategory, existingCategoryLinks),
    [viewAnalysis.products, existingProductsByCategory, existingCategoryLinks],
  );
  const handleToggleCategoryLink = useCallback((detectedCategoryId: string, link: string | null): void => {
    setLinkOverrides((prev) => ({ ...prev, [detectedCategoryId]: link }));
  }, []);

  // Tracking de montaje + nota de cooldown soft (no bloquea).
  useEffect(() => {
    trackEvent(TRACK_EVENT_NAMES.SmartMenuViewed, { source: trackingSource });
    const cd = getCooldown();
    if (cd.active) {
      const minutes = Math.max(1, Math.ceil(cd.remainingMs / 60000));
      toast(`Intentaste hace poco. Si quieres, espera ~${minutes} min antes de reintentar.`);
    }
    // Limpia procesos en vuelo y timeouts pendientes si el componente se desmonta a mitad.
    return () => {
      abortAll();
      if (completionTimeoutRef.current !== null) {
        window.clearTimeout(completionTimeoutRef.current);
        completionTimeoutRef.current = null;
      }
    };
  }, []);

  // Cuando arranca el procesamiento por primera vez, pasamos a phase 'processing'.
  useEffect(() => {
    if (phase === 'upload' && isProcessing && !startedRef.current) {
      startedRef.current = true;
      setPhase('processing');
    }
  }, [phase, isProcessing]);

  // Cuando el procesamiento termina (todo done|failed), decidimos siguiente phase.
  useEffect(() => {
    if (phase !== 'processing') return;
    const { done, failed, total } = totalProgress;
    if (total === 0) return;
    if (done + failed < total) return;
    // Si el batch falló por completo (single-call: failed=1, done=0): NO
    // transicionar. Quedarse en 'processing' para que el componente muestre el
    // mensaje de error real del backend (rate limit, credenciales, etc.) y el
    // usuario pueda reintentar o reiniciar sin caer a low-extraction con 0
    // products extraídos (que confunde "qué falló").
    if (failed > 0 && done === 0) return;
    if (transitionedRef.current) return;
    transitionedRef.current = true;

    // jobId activo: tras un resume viene de lastResumedRef; en flujo normal,
    // de la referencia que onJobStart persistió. Alimenta el draft de review.
    const jobId = lastResumedRef.current?.jobId ?? loadPendingJob()?.jobId ?? null;
    activeJobIdRef.current = jobId;

    // Si hay draft del MISMO job (reload durante review), rehidratar la curaduría
    // en lugar de inicializar "todo seleccionado" (spec UX 2026-06-10 §6.2).
    const draft = jobId ? loadReviewDraft(jobId) : null;
    let draftApplied = false;
    if (draft) {
      // Filtrar el draft contra el análisis VIGENTE: ids de batches aditivos
      // perdidos (no recuperables post-reload) o de prefijos viejos no deben
      // colarse a la selección — inflarían los conteos de publicación con
      // entidades fantasma. Las categorías sintéticas del draft cuentan como
      // vigentes (se re-agregan vía setSyntheticCategories abajo).
      const validCategoryIds = new Set([
        ...analysis.categories.map((c) => c.id),
        ...(draft.syntheticCategories ?? []).map((c) => c.id),
      ]);
      const validProductIds = new Set(analysis.products.map((p) => p.id));
      const validExtraIds = new Set(analysis.extras.map((e) => e.id));
      const validSectionIds = new Set(analysis.sections.map((s) => s.id));
      const draftProducts = draft.selectedProductIds.filter((id) => validProductIds.has(id));
      // Draft de un esquema de ids viejo: tenía productos seleccionados pero
      // NINGUNO re-matchea con el análisis vigente. Aplicarlo dejaría "0 de N
      // incluidos" — peor que empezar limpio. Se descarta y se cae al default.
      const draftIsStale =
        analysis.products.length > 0 && draft.selectedProductIds.length > 0 && draftProducts.length === 0;
      if (draftIsStale) {
        clearReviewDraft();
      } else {
        setSelectedCategoryIds(new Set(draft.selectedCategoryIds.filter((id) => validCategoryIds.has(id))));
        setSelectedProductIds(new Set(draftProducts));
        setSelectedExtraIds(new Set(draft.selectedExtraIds.filter((id) => validExtraIds.has(id))));
        setSelectedSectionIds(new Set(draft.selectedSectionIds.filter((id) => validSectionIds.has(id))));
        setEditedProductPatches(draft.editedProductPatches ?? {});
        setEditedCategoryNames(draft.editedCategoryNames ?? {});
        setEditedExtraPatches(draft.editedExtraPatches ?? {});
        setEditedSectionNames(draft.editedSectionNames ?? {});
        setDuplicateDecisions(draft.duplicateDecisions ?? {});
        setCategoryAssignments(draft.categoryAssignments ?? {});
        setSyntheticCategories(draft.syntheticCategories ?? []);
        // WS-B: restaura SOLO los overrides cuya categoría DETECTADA sigue vigente.
        // El target (existingCatId) NO se valida aquí — el catálogo puede no haber
        // cargado aún en este efecto; la validez del target se garantiza en uso
        // (guard del publish + el chip resuelve el nombre vía existingCategoriesById). (R3)
        const draftLinkOverrides = draft.existingCategoryLinkOverrides ?? {};
        const validLinkOverrides: Record<string, string | null> = {};
        for (const [detectedId, target] of Object.entries(draftLinkOverrides)) {
          if (validCategoryIds.has(detectedId)) validLinkOverrides[detectedId] = target;
        }
        setLinkOverrides(validLinkOverrides);
        draftApplied = true;
        toast.success('Recuperamos tu avance');
      }
    }
    if (!draftApplied) {
      // Inicializa selección con TODOS los IDs detectados.
      setSelectedCategoryIds(buildIdSet(analysis.categories.map((c) => c.id)));
      setSelectedProductIds(buildIdSet(analysis.products.map((p) => p.id)));
      setSelectedExtraIds(buildIdSet(analysis.extras.map((e) => e.id)));
      setSelectedSectionIds(buildIdSet(analysis.sections.map((s) => s.id)));
    }

    // Marca los batches ya integrados a la selección: los batches ADITIVOS que
    // completen DESPUÉS (durante review) suman sus ids vía el efecto de abajo
    // sin resetear la curaduría.
    integratedItemIdsRef.current = new Set(items.filter((it) => it.status === 'completed').map((it) => it.id));
    lastDuplicateGroupsRef.current = analysis.duplicateGroups;

    trackEvent(TRACK_EVENT_NAMES.SmartMenuProcessOk, {
      products: analysis.products.length,
      categories: analysis.categories.length,
      extras: analysis.extras.length,
      sections: analysis.sections.length,
      duration_ms: Date.now() - startTime,
    });

    if (analysis.products.length < LOW_EXTRACTION_THRESHOLD) {
      setPhase('low-extraction');
    } else {
      setPhase('review');
    }
  }, [phase, totalProgress, analysis, items, startTime, trackEvent]);

  // Error de análisis (batch principal fallido): un evento por fallo concreto
  // (guard por finishedAt para no re-emitir en cada render del estado failed).
  const trackedProcessFailRef = useRef<string | null>(null);
  useEffect(() => {
    if (phase !== 'processing') return;
    const failedItem = items.find((it) => it.status === 'failed');
    if (!failedItem) return;
    const failKey = `${failedItem.id}_${failedItem.finishedAt ?? 0}`;
    if (trackedProcessFailRef.current === failKey) return;
    trackedProcessFailRef.current = failKey;
    trackEvent(TRACK_EVENT_NAMES.SmartMenuProcessError, {
      error_kind: failedItem.error?.kind ?? 'unknown',
      files_count: selectedFiles.length,
      duration_ms: Date.now() - startTime,
    });
  }, [phase, items, selectedFiles.length, startTime, trackEvent]);

  // Integración de batches ADITIVOS (spec §6.1): cuando un append completa
  // durante review, sus entidades nuevas entran SELECCIONADAS sin tocar la
  // curaduría existente (ids con prefijo `${batchId}__` de consolidate; los
  // duplicados contra lo previo ya quedaron fusionados al representante).
  useEffect(() => {
    if (phase !== 'review' && phase !== 'low-extraction') return;
    const fresh = items.filter((it) => it.status === 'completed' && !integratedItemIdsRef.current.has(it.id));
    if (fresh.length === 0) return;
    fresh.forEach((it) => integratedItemIdsRef.current.add(it.id));
    const isNew = (id: string): boolean => fresh.some((it) => id.startsWith(`${it.id}__`));
    const union = (prev: Set<string>, ids: string[]): Set<string> => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    };
    const newCategoryIds = enrichedAnalysis.categories.filter((c) => isNew(c.id)).map((c) => c.id);
    const newProductIds = enrichedAnalysis.products.filter((p) => isNew(p.id)).map((p) => p.id);
    const newExtraIds = enrichedAnalysis.extras.filter((e) => isNew(e.id)).map((e) => e.id);
    const newSectionIds = enrichedAnalysis.sections.filter((s) => isNew(s.id)).map((s) => s.id);
    if (newCategoryIds.length > 0) setSelectedCategoryIds((prev) => union(prev, newCategoryIds));
    if (newProductIds.length > 0) setSelectedProductIds((prev) => union(prev, newProductIds));
    if (newExtraIds.length > 0) setSelectedExtraIds((prev) => union(prev, newExtraIds));
    if (newSectionIds.length > 0) setSelectedSectionIds((prev) => union(prev, newSectionIds));

    // Las decisiones de duplicados van keyed por índice: grupos nuevos pueden
    // desplazar índices → re-mapear por identidad estable del grupo.
    const previousGroups = lastDuplicateGroupsRef.current;
    lastDuplicateGroupsRef.current = enrichedAnalysis.duplicateGroups;
    setDuplicateDecisions((prev) =>
      Object.keys(prev).length > 0
        ? remapDuplicateDecisions(previousGroups, enrichedAnalysis.duplicateGroups, prev)
        : prev,
    );

    if (newProductIds.length > 0) {
      toast.success(
        `Agregamos ${newProductIds.length} producto${newProductIds.length === 1 ? '' : 's'} nuevo${newProductIds.length === 1 ? '' : 's'}`,
      );
    } else {
      toast('Los archivos nuevos no agregaron productos (ya estaban en tu menú)');
    }
  }, [phase, items, enrichedAnalysis]);

  // Draft de curaduría: cada cambio en review se persiste (debounce 500ms) para
  // sobrevivir reloads. Solo mientras hay review activo con jobId conocido.
  useEffect(() => {
    if (phase !== 'review') return;
    const jobId = activeJobIdRef.current;
    if (!jobId) return;
    const timeout = window.setTimeout(() => {
      saveReviewDraft({
        jobId,
        savedAt: Date.now(),
        selectedCategoryIds: Array.from(selectedCategoryIds),
        selectedProductIds: Array.from(selectedProductIds),
        selectedExtraIds: Array.from(selectedExtraIds),
        selectedSectionIds: Array.from(selectedSectionIds),
        editedProductPatches,
        editedCategoryNames,
        editedExtraPatches,
        editedSectionNames,
        duplicateDecisions,
        categoryAssignments,
        syntheticCategories,
        existingCategoryLinkOverrides: linkOverrides,
      });
    }, 500);
    return () => window.clearTimeout(timeout);
  }, [
    phase,
    selectedCategoryIds,
    selectedProductIds,
    selectedExtraIds,
    selectedSectionIds,
    editedProductPatches,
    editedCategoryNames,
    editedExtraPatches,
    editedSectionNames,
    duplicateDecisions,
    categoryAssignments,
    syntheticCategories,
    linkOverrides,
  ]);

  const onFilesAdded = useCallback(
    (files: File[]): void => {
      if (files.length === 0) return;
      const counts = sumPdfImagesSize(files);
      trackEvent(TRACK_EVENT_NAMES.SmartMenuFilesAdded, {
        pdf_count: counts.pdf,
        image_count: counts.image,
        total_size_mb: Number(counts.totalMB.toFixed(2)),
      });
      // Single-call: NO encolamos en el hook todavía. El batch real arranca
      // cuando el usuario presiona "Procesar ahora". Esto permite editar la
      // selección (remover archivos) sin disparar la llamada al backend.
      setSelectedFiles((prev) => [...prev, ...files]);
    },
    [trackEvent],
  );

  const handleStartProcessing = useCallback((): void => {
    if (selectedFiles.length === 0) return;
    // El cooldown se arma aquí (submit real) y no al seleccionar archivos:
    // seleccionar y abandonar sin procesar no cuenta como intento.
    markAttempt();
    trackEvent(TRACK_EVENT_NAMES.SmartMenuProcessStart, { files_count: selectedFiles.length });
    // Batch nuevo: cualquier estado de resume previo deja de aplicar.
    lastResumedRef.current = null;
    setResumedPreviews({});
    setPendingJob(null);
    // Dispara la llamada UNA sola vez con todos los archivos seleccionados.
    // El hook gestiona el ciclo de vida del batch (uploading → completed/failed).
    startedRef.current = true;
    enqueue(selectedFiles);
    setPhase('processing');
  }, [enqueue, selectedFiles]);

  // Retoma el job persistido tras un reload: reconstruye el grid con stubs (el
  // preview viene de las download URLs de Storage) y recupera el resultado por
  // jobId — sin re-subir archivos ni re-pagar el procesamiento LLM.
  const handleRecoverPending = useCallback((): void => {
    const pending = pendingJob;
    if (!pending) return;
    lastResumedRef.current = pending;
    trackEvent(TRACK_EVENT_NAMES.SmartMenuRecover, {
      files_count: pending.files.length,
      age_min: Math.round((Date.now() - pending.startedAt) / 60000),
    });
    setSelectedFiles(
      pending.files.map((f) => new File([], f.name, { type: f.kind === 'pdf' ? ACCEPTED_PDF_MIME : 'image/jpeg' })),
    );
    setResumedPreviews(Object.fromEntries(pending.files.map((f) => [f.name, f.url])));
    setPendingJob(null);
    startedRef.current = true;
    resume(pending);
    setPhase('processing');
  }, [pendingJob, resume, trackEvent]);

  const handleDiscardPending = useCallback((): void => {
    clearPendingJob();
    clearReviewDraft();
    setPendingJob(null);
  }, []);

  const handleSkip = useCallback((): void => {
    trackEvent(TRACK_EVENT_NAMES.SmartMenuSkipped, {
      had_files_selected: selectedFiles.length > 0,
    });
    abortAll();
    const result: SmartMenuCompletionResult = {
      skipped: true,
      productsCreated: 0,
      categoriesCreated: 0,
      extrasCreated: 0,
      sectionsCreated: 0,
      durationMs: Date.now() - startTime,
    };
    // Single source of truth: onComplete con result.skipped=true. El caller
    // distingue skip de publish leyendo result.skipped. Evita el doble callback
    // que detecto Seer (comment 3372844752). `onSkip` queda deprecated en el API.
    onComplete(result);
  }, [abortAll, onComplete, selectedFiles.length, startTime, trackEvent]);

  const toggleSetMember = (setter: Dispatch<SetStateAction<Set<string>>>) => (id: string) => {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Toggle de categoria con cascade SIMÉTRICO: deseleccionar quita también a los
  // productos hijos (previene huérfanos silently dropped en persist, Seer
  // 3372844745); seleccionar re-incluye a los hijos (el checkbox tri-estado del
  // header funciona como bulk de la categoría completa).
  const onToggleCategory = useCallback(
    (categoryId: string): void => {
      const wasSelected = selectedCategoryIds.has(categoryId);
      setSelectedCategoryIds((prev) => {
        const next = new Set(prev);
        if (wasSelected) next.delete(categoryId);
        else next.add(categoryId);
        return next;
      });
      setSelectedProductIds((prev) => {
        const next = new Set(prev);
        for (const product of enrichedAnalysis.products) {
          if (product.category !== categoryId) continue;
          if (wasSelected) next.delete(product.id);
          else next.add(product.id);
        }
        return next;
      });
    },
    [selectedCategoryIds, enrichedAnalysis.products],
  );
  const onToggleProduct = useMemo(() => toggleSetMember(setSelectedProductIds), []);
  const onToggleExtra = useMemo(() => toggleSetMember(setSelectedExtraIds), []);
  const onToggleSection = useMemo(() => toggleSetMember(setSelectedSectionIds), []);

  // Reasignación de categoría (individual desde el EditSheet o bulk desde el
  // bucket de huérfanos). Vive como override encima del análisis: el id del
  // producto no cambia, así que selecciones/ediciones previas se conservan.
  const onReassignCategory = useCallback((productIds: string[], newCategoryId: string): void => {
    if (productIds.length === 0 || !newCategoryId) return;
    setCategoryAssignments((prev) => {
      const next = { ...prev };
      productIds.forEach((id) => {
        next[id] = newCategoryId;
      });
      return next;
    });
  }, []);

  // "+ Crear categoría nueva…" (spec §2.4): DetectedCategory sintética
  // client-side con id `synthetic_<slug>`; entra seleccionada. Devuelve el id
  // para que el EditSheet reasigne el producto en el mismo gesto.
  const onCreateCategory = useCallback(
    (name: string): string | null => {
      const existingIds = new Set(enrichedAnalysis.categories.map((c) => c.id));
      const category = buildSyntheticCategory(name, existingIds);
      if (!category) return null;
      setSyntheticCategories((prev) => [...prev, category]);
      setSelectedCategoryIds((prev) => {
        const next = new Set(prev);
        next.add(category.id);
        return next;
      });
      return category.id;
    },
    [enrichedAnalysis.categories],
  );

  // Bulk global "Todo": incluye/excluye TODAS las entidades de un golpe.
  // Mantiene la selección internamente consistente (un producto jamás queda
  // seleccionado sin su categoría por esta vía).
  const onToggleAll = useCallback(
    (selectAll: boolean): void => {
      if (selectAll) {
        setSelectedCategoryIds(buildIdSet(viewAnalysis.categories.map((c) => c.id)));
        setSelectedProductIds(buildIdSet(viewAnalysis.products.map((p) => p.id)));
        setSelectedExtraIds(buildIdSet(viewAnalysis.extras.map((e) => e.id)));
        setSelectedSectionIds(buildIdSet(viewAnalysis.sections.map((s) => s.id)));
      } else {
        setSelectedCategoryIds(new Set());
        setSelectedProductIds(new Set());
        setSelectedExtraIds(new Set());
        setSelectedSectionIds(new Set());
      }
    },
    [viewAnalysis],
  );

  const onEditProduct = useCallback(
    (id: string, patch: { nombre?: string; precio?: number; opciones?: ProductPresentation[] }): void => {
      setEditedProductPatches((prev) => ({
        ...prev,
        [id]: { ...prev[id], ...patch },
      }));
      trackEvent(TRACK_EVENT_NAMES.SmartMenuEdit, { tipo: 'producto' });
    },
    [trackEvent],
  );

  const onEditCategory = useCallback((id: string, patch: { nombre?: string }): void => {
    if (typeof patch.nombre === 'string') {
      setEditedCategoryNames((prev) => ({ ...prev, [id]: patch.nombre as string }));
    }
  }, []);

  const onEditExtra = useCallback((id: string, patch: { nombre?: string; precio?: number }): void => {
    setEditedExtraPatches((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...patch },
    }));
  }, []);

  const onEditSection = useCallback((id: string, patch: { nombre?: string }): void => {
    if (typeof patch.nombre === 'string') {
      setEditedSectionNames((prev) => ({ ...prev, [id]: patch.nombre as string }));
    }
  }, []);

  const onDecideDuplicate = useCallback((groupIndex: number, decision: 'keep_representative' | 'keep_all'): void => {
    setDuplicateDecisions((prev) => ({ ...prev, [groupIndex]: decision }));
  }, []);

  // Reset completo del flujo: cancela jobs en vuelo, vacia la cola, limpia
  // selección/ediciones/decisiones y vuelve a 'upload'. No abre confirm modal —
  // sesiones previas con el agente Negocio rechazaron confirmaciones destructivas
  // como "paternalistas". El usuario que pulsa "Empezar de cero" sabe lo que hace.
  const handleRestart = useCallback((): void => {
    if (phase === 'persisting') return; // guard: no se puede reiniciar en mitad de persist (in-flight).
    abortAll();
    reset();
    setSelectedFiles([]);
    // Estado de resume/draft: reset() ya limpió el pending job persistido.
    clearReviewDraft();
    lastResumedRef.current = null;
    activeJobIdRef.current = null;
    setResumedPreviews({});
    setPendingJob(null);
    setPhase('upload');
    setSelectedCategoryIds(new Set());
    setSelectedProductIds(new Set());
    setSelectedExtraIds(new Set());
    setSelectedSectionIds(new Set());
    setEditedProductPatches({});
    setEditedCategoryNames({});
    setEditedExtraPatches({});
    setEditedSectionNames({});
    setDuplicateDecisions({});
    setCategoryAssignments({});
    setSyntheticCategories([]);
    setAppendedBatches({});
    setAppendSheetOpen(false);
    setAppendSheetFiles([]);
    setPublishError(null);
    setStartTime(Date.now());
    startedRef.current = false;
    transitionedRef.current = false;
    completedRef.current = false;
    persistingRef.current = false;
    integratedItemIdsRef.current = new Set();
    lastDuplicateGroupsRef.current = [];
    // Limpiamos el tracking de idempotencia: tras restart, la siguiente persist
    // arranca desde cero y crea todas las entidades de nuevo (es lo que el user quiere).
    createdEntitiesRef.current = {
      categoryMap: new Map(),
      productIds: new Set(),
      productMap: new Map(),
      extraIds: new Set(),
      extraMap: new Map(),
      sectionMap: new Map(),
      sectionIds: new Set(),
      linkedCategoryIds: new Set(),
    };
    // Cancela el onComplete diferido si el restart ocurre dentro de los DONE_DELAY_MS
    // (Seer comment 3375274913). Sin esto, el onComplete stale dispararia con datos del
    // intento previo y habilitaria erroneamente el boton "Finalizar" del wizard padre.
    if (completionTimeoutRef.current !== null) {
      window.clearTimeout(completionTimeoutRef.current);
      completionTimeoutRef.current = null;
    }
    trackEvent(TRACK_EVENT_NAMES.SmartMenuRestarted, {
      from_phase: phase,
      files_in_queue: selectedFiles.length,
    });
  }, [abortAll, phase, reset, selectedFiles.length, trackEvent]);

  const renderRestartButton = (): ReactElement => (
    <Button
      data-testid="smart-menu-restart"
      variant="ghost"
      iconLeft={<IconRefresh size={16} aria-hidden="true" />}
      onClick={handleRestart}
    >
      Volver a empezar
    </Button>
  );

  const handlePersist = useCallback(async (): Promise<void> => {
    // Anti re-entry: guard sincronico contra double-click. completedRef cubre
    // re-trigger tras exito; persistingRef cubre clicks rapidos antes que
    // setPhase('persisting') oculte el boton (Seer comment 3375220929).
    if (completedRef.current || persistingRef.current) return;
    persistingRef.current = true;
    setPublishError(null);
    trackEvent(TRACK_EVENT_NAMES.SmartMenuPublishStart, {
      products: viewAnalysis.products.filter((p) => selectedProductIds.has(p.id)).length,
    });
    setPhase('persisting');

    // `viewAnalysis` ya trae categorías sintéticas + reasignaciones + ORDEN del
    // usuario: iterar sus arrays en orden ES respetar el reorden al publicar.
    const filtered = applyDuplicateDecisionsToAnalysis(viewAnalysis, duplicateDecisions);
    const selectedCategories = filtered.categories.filter((c) => selectedCategoryIds.has(c.id));
    const selectedProducts = filtered.products.filter((p) => selectedProductIds.has(p.id));
    const selectedExtras = filtered.extras.filter((e) => selectedExtraIds.has(e.id));
    // sections vienen de `viewAnalysis.sections` (filtered.sections === viewAnalysis.sections,
    // ya que `applyDuplicateDecisionsToAnalysis` no toca sections — el dedup actual
    // no las cubre).
    const selectedSections = viewAnalysis.sections.filter((s) => selectedSectionIds.has(s.id));

    // BLINDAJE anti drop-silencioso: un producto seleccionado SIEMPRE se publica
    // con su categoría, y un modificador seleccionado SIEMPRE con sus opciones
    // (extras). Estados de selección inconsistentes son alcanzables (drafts de
    // esquemas viejos, bulk parcial) y antes degeneraban en "Publicar (101)" que
    // creaba 6: los productos sin categoría creada se saltaban en silencio.
    const categoriesToCreate = withAutoIncluded(
      selectedCategories,
      filtered.categories,
      new Set(selectedProducts.map((p) => p.category).filter((id): id is string => Boolean(id))),
    );
    const extrasToCreate = withAutoIncluded(
      selectedExtras,
      filtered.extras,
      new Set(selectedSections.flatMap((s) => s.opciones)),
    );

    // Idempotencia: usar el ref que persiste entre intentos. Si la persist anterior
    // fallo a mitad, este intento continua donde quedo (skip de lo ya creado) en
    // lugar de duplicar entidades en backend.
    const oldToNewCategoryId = createdEntitiesRef.current.categoryMap;
    const createdProductIds = createdEntitiesRef.current.productIds;
    const createdProductsByOldId = createdEntitiesRef.current.productMap;
    const createdExtraIds = createdEntitiesRef.current.extraIds;
    const oldToNewExtraId = createdEntitiesRef.current.extraMap;
    const oldToNewSectionId = createdEntitiesRef.current.sectionMap;
    const createdSectionIds = createdEntitiesRef.current.sectionIds;
    const linkedCategoryIds = createdEntitiesRef.current.linkedCategoryIds;
    // WS-B guard: un link solo es válido si su target sigue siendo una categoría
    // existente ACTIVA. Si el catálogo cambió entre sesiones, el link stale cae a la
    // creación normal (no se reusa un id fantasma).
    const activeExistingIds = new Set(existingCategories.filter((c) => c.estatus === 'activo').map((c) => c.id));
    // WS-B (R2): reconciliar links stale de intentos previos ANTES de los contadores. Si
    // una categoría linkeada en un intento anterior ya NO está linkeada (el usuario la
    // desvinculó) o su target dejó de ser válido (existente desactivada/borrada), se quita
    // de la memoria de idempotencia para que el loop la re-evalúe (re-link o crear nueva).
    // Limitación conocida: los productos ya creados en un intento previo bajo el link viejo
    // NO se recategorizan (ya están en BD); esto solo corrige lo que se cree en el reintento.
    reconcileStaleCategoryLinks(linkedCategoryIds, oldToNewCategoryId, existingCategoryLinks, activeExistingIds);
    // `categoriesLinked` (reutilizadas) se deriva al final como ids existentes DISTINTOS (R5).
    let categoriesCreated = oldToNewCategoryId.size - linkedCategoryIds.size;
    let productsCreated = createdProductIds.size;
    let extrasCreated = createdExtraIds.size;
    let sectionsCreated = oldToNewSectionId.size;

    try {
      for (const [catIndex, cat] of categoriesToCreate.entries()) {
        if (oldToNewCategoryId.has(cat.id)) continue; // ya creado/linkeado en intento previo
        const linkedExistingId = existingCategoryLinks[cat.id];
        if (linkedExistingId && activeExistingIds.has(linkedExistingId)) {
          // WS-B: categoría detectada asociada a una existente → reusa su id (los
          // productos se enganchan vía `oldToNewCategoryId.get(prod.category)` en el
          // loop de productos, sin cambios) y se SALTA el POST (no duplica).
          oldToNewCategoryId.set(cat.id, linkedExistingId);
          linkedCategoryIds.add(cat.id);
          continue;
        }
        const nombre = editedCategoryNames[cat.id] ?? cat.name;
        const payload: Category = {
          id: '',
          nombre,
          // Orden EXPLÍCITO (1-based) según el orden de detección del menú: sin
          // esto, el orden publicado dependería de que el backend auto-asigne
          // por secuencia de creación (frágil ante reintentos parciales).
          orden: catIndex + 1,
          estatus: 'activo',
          disponibilidad_por_dias: false,
          disponibilidad_por_horas: false,
          hora_inicio: '',
          hora_fin: '',
          dias_disponibles: [],
        };
        const created = await createCategoryCall({ data: payload });
        if (!created?.id) throw new Error(`No se pudo crear la categoría "${nombre}"`);
        oldToNewCategoryId.set(cat.id, created.id);
        categoriesCreated += 1;
      }

      for (const prod of selectedProducts) {
        if (createdProductIds.has(prod.id)) continue; // ya persistido en intento previo
        const { nombre, opcionesPayload } = buildProductCreateFields(prod, editedProductPatches[prod.id]);
        const newCategoryId = prod.category ? oldToNewCategoryId.get(prod.category) : undefined;
        if (!newCategoryId) {
          // El producto perdió su categoría (deseleccionada o nunca tuvo) → lo saltamos.
          continue;
        }
        const payload: Product = {
          id: '',
          id_categoria: newCategoryId,
          nombre,
          descripcion: prod.description ?? '',
          estatus: 'activo',
          imagen: null,
          disponibilidad_por_dias: false,
          dias_disponibles: [],
          id_secciones: [],
          available_for: ['inRestaurant', 'takeAway', 'delivery'],
          opciones: opcionesPayload,
        };
        const created = await createProductCall({ data: payload });
        if (!created?.id) throw new Error(`No se pudo crear el producto "${nombre}"`);
        createdProductIds.add(prod.id);
        createdProductsByOldId.set(prod.id, created);
        productsCreated += 1;
      }

      for (const extra of extrasToCreate) {
        if (createdExtraIds.has(extra.id)) continue; // ya persistido en intento previo
        const edit = editedExtraPatches[extra.id] ?? {};
        const nombre = edit.nombre ?? extra.name;
        const precio =
          typeof edit.precio === 'number' ? edit.precio : typeof extra.price === 'number' ? extra.price : 0;
        const payload: Extra = {
          id: '',
          id_comercio: '',
          nombre,
          precio,
          costo_produccion: 0,
          estatus: 'activo',
        };
        const created = await createExtraCall({ data: payload });
        if (!created?.id) throw new Error(`No se pudo crear el extra "${nombre}"`);
        createdExtraIds.add(extra.id);
        oldToNewExtraId.set(extra.id, created.id);
        extrasCreated += 1;
      }

      // V4: persist de sections (modificadores). Idempotente igual que los loops anteriores.
      // SIMPLIFICACION (TODO próxima iteración): persistimos sections sin `opciones[]`.
      // El mapeo `oldSyntheticExtraId -> newDbExtraId` no existe hoy en `createdEntitiesRef`
      // (sólo trackeamos `createdExtraIds: Set<oldId>`, no el ID real devuelto por el POST).
      // Sin ese mapeo, no podemos traducir `section.opciones: string[]` (IDs sintéticos) a
      // IDs reales de DB. Preferimos crear la section vacía a no crearla: el usuario verá el
      // modificador en su menú y podrá ligar opciones manualmente desde el panel.
      // Próxima iteración: extender `createdEntitiesRef.extraMap: Map<oldId, newDbId>` (mirror
      // de categoryMap) y poblar `opciones` aquí con los IDs reales.
      for (const sec of selectedSections) {
        if (createdSectionIds.has(sec.id)) continue;
        const nombre = editedSectionNames[sec.id] ?? sec.name;
        const payload: Section = {
          id: '',
          id_comercio: '',
          nombre,
          numero_de_opciones: sec.numero_de_opciones,
          tipo: sec.tipo,
          obligatorio: sec.obligatorio,
          es_multiplo: sec.es_multiplo,
          estatus: 'activo',
          // Opciones REALES del modificador: ref del análisis → representante
          // del dedupe (extrasRemap) → ID de BD del extra creado (incluye los
          // auto-incluidos por el blindaje). Antes iba [] con un TODO sin
          // implementar: los modificadores se publicaban VACÍOS (Seer 3400198861).
          opciones: sec.opciones
            .map((oldExtraId) => filtered.extrasRemap?.[oldExtraId] ?? oldExtraId)
            .map((extraId) => oldToNewExtraId.get(extraId))
            .filter((dbId): dbId is string => Boolean(dbId)),
        };
        const created = await createSectionCall({ data: payload });
        if (!created?.id) throw new Error(`No se pudo crear la sección "${nombre}"`);
        oldToNewSectionId.set(sec.id, created.id);
        createdSectionIds.add(sec.id);
        sectionsCreated += 1;
      }

      // Binding productos ↔ modificadores: el POST de producto ocurre ANTES de
      // que existan los IDs de sección en BD (id_secciones iba [] hardcodeado y
      // los modificadores publicados no aparecían en ningún producto — Seer
      // 3400198861, CRITICAL). Segundo paso: PUT por producto con sus secciones.
      await bindSectionsToProducts(selectedSections, oldToNewSectionId, createdProductsByOldId, updateProductCall);

      const durationMs = Date.now() - startTime;
      // R5: "reutilizadas" = categorías existentes DISTINTAS reusadas (no ids detectados;
      // evita doble conteo en el caso contrived de dos detectadas que mapeen a la misma
      // existente, p. ej. una sintética + una detectada con el mismo nombre).
      const categoriesLinked = new Set(
        [...linkedCategoryIds].map((id) => oldToNewCategoryId.get(id)).filter((x): x is string => Boolean(x)),
      ).size;
      trackEvent(TRACK_EVENT_NAMES.SmartMenuPublishOk, {
        files_count: selectedFiles.length,
        duration_ms: durationMs,
        categories_created: categoriesCreated,
        categories_linked: categoriesLinked,
        products_created: productsCreated,
        extras_created: extrasCreated,
        sections_created: sectionsCreated,
      });

      completedRef.current = true;
      persistingRef.current = false;
      // Menú publicado: el job y el draft de curaduría cumplieron su ciclo.
      clearPendingJob();
      clearReviewDraft();
      activeJobIdRef.current = null;
      // Limpiamos el tracking de idempotencia: exito completo, futuro restart sera limpio.
      createdEntitiesRef.current = {
        categoryMap: new Map(),
        productIds: new Set(),
        productMap: new Map(),
        extraIds: new Set(),
        extraMap: new Map(),
        sectionMap: new Map(),
        sectionIds: new Set(),
        linkedCategoryIds: new Set(),
      };
      // Guardamos los counts reales persistidos para mostrar en la pantalla 'done'
      // (no los detectados originalmente — Seer comment 3375387525).
      setPersistedCounts({
        categories: categoriesCreated,
        categoriesLinked,
        products: productsCreated,
        extras: extrasCreated,
        sections: sectionsCreated,
      });
      setPhase('done');
      completionTimeoutRef.current = window.setTimeout(() => {
        completionTimeoutRef.current = null;
        onComplete({
          skipped: false,
          productsCreated,
          categoriesCreated,
          categoriesLinked,
          extrasCreated,
          sectionsCreated,
          durationMs,
        });
      }, DONE_DELAY_MS);
    } catch (err) {
      // Mantenemos createdEntitiesRef intacto para que un retry continue donde quedo
      // sin duplicar. persistingRef se libera para permitir el retry.
      persistingRef.current = false;
      const message = err instanceof Error ? err.message : 'No se pudo publicar el menú';
      trackEvent(TRACK_EVENT_NAMES.SmartMenuPublishError, {
        files_count: selectedFiles.length,
        duration_ms: Date.now() - startTime,
        products_total: viewAnalysis.products.length,
      });
      // Banner persistente sobre la barra de publicar (spec §6.4) — reemplaza
      // al toast efímero. REINTENTAR reusa la idempotencia de createdEntitiesRef
      // (continúa donde quedó, sin duplicar lo ya creado).
      setPublishError({
        failedProducts: selectedProducts.filter((p) => !createdProductIds.has(p.id)).length,
        totalProducts: selectedProducts.length,
        message,
      });
      setPhase('review');
    }
  }, [
    viewAnalysis,
    createCategoryCall,
    createExtraCall,
    createProductCall,
    createSectionCall,
    updateProductCall,
    duplicateDecisions,
    editedCategoryNames,
    editedExtraPatches,
    editedProductPatches,
    editedSectionNames,
    selectedFiles.length,
    onComplete,
    selectedCategoryIds,
    selectedExtraIds,
    selectedProductIds,
    selectedSectionIds,
    startTime,
    trackEvent,
    existingCategoryLinks,
    existingCategories,
  ]);

  const counts = useMemo(() => {
    let pdf = 0;
    let image = 0;
    let totalMB = 0;
    selectedFiles.forEach((f) => {
      if (f.type === ACCEPTED_PDF_MIME) pdf += 1;
      else image += 1;
      totalMB += f.size / (1024 * 1024);
    });
    return { pdf, image, totalMB };
  }, [selectedFiles]);

  // Items sintéticos del upload phase: el hook todavía no ha encolado el batch
  // (eso pasa al click "Procesar ahora"), pero queremos que FileQueueGrid muestre
  // cada archivo seleccionado para que el usuario pueda removerlo antes de procesar.
  // Cada item se renderiza como `queued` (estado neutral) sin status badges activos.
  //
  // El `id` usa name+size+lastModified como clave estable (NO el índice del array)
  // para que clicks rápidos consecutivos en distintos items no se pisen entre sí
  // por race condition de `setSelectedFiles` con índices stale.
  const uploadDisplayItems: UploadQueueItem[] = useMemo(
    () =>
      selectedFiles.map((file) => ({
        id: `selected_${file.name}_${file.size}_${file.lastModified}`,
        file,
        kind: file.type === ACCEPTED_PDF_MIME ? 'pdf' : 'image',
        sizeMB: Math.round((file.size / (1024 * 1024)) * 100) / 100,
        status: 'queued' as const,
        attempt: 0,
      })),
    [selectedFiles],
  );

  // Remueve un archivo del array `selectedFiles` por id estable (name+size+lastModified)
  // del display item. Inmune a race condition entre clicks consecutivos: cada
  // setSelectedFiles funcional opera sobre el estado más reciente y matchea por
  // identidad inmutable, no por posición.
  const removeSelectedFile = useCallback((id: string): void => {
    setSelectedFiles((prev) => prev.filter((f) => `selected_${f.name}_${f.size}_${f.lastModified}` !== id));
  }, []);

  // ---- Batches aditivos desde review ("+ Agregar fotos o PDF", spec §6.1) ----

  // Archivos ya comprometidos (batch principal + appends lanzados + selección
  // actual del sheet): el uploader del sheet recibe estos conteos para que los
  // límites del flujo completo (maxPdfs/maxImages/maxTotalMB) se respeten de
  // forma RESTANTE, no por batch.
  const appendUsage = useMemo(() => {
    const committed = [...selectedFiles, ...Object.values(appendedBatches).flat(), ...appendSheetFiles];
    let pdf = 0;
    let image = 0;
    let totalMB = 0;
    committed.forEach((f) => {
      if (f.type === ACCEPTED_PDF_MIME) pdf += 1;
      else image += 1;
      totalMB += f.size / (1024 * 1024);
    });
    return { pdf, image, totalMB };
  }, [selectedFiles, appendedBatches, appendSheetFiles]);

  const appendSheetDisplayItems: UploadQueueItem[] = useMemo(
    () =>
      appendSheetFiles.map((file) => ({
        id: `selected_${file.name}_${file.size}_${file.lastModified}`,
        file,
        kind: file.type === ACCEPTED_PDF_MIME ? 'pdf' : 'image',
        sizeMB: Math.round((file.size / (1024 * 1024)) * 100) / 100,
        status: 'queued' as const,
        attempt: 0,
      })),
    [appendSheetFiles],
  );

  const onAppendFilesAdded = useCallback((files: File[]): void => {
    if (files.length === 0) return;
    setAppendSheetFiles((prev) => [...prev, ...files]);
  }, []);

  const removeAppendSheetFile = useCallback((id: string): void => {
    setAppendSheetFiles((prev) => prev.filter((f) => `selected_${f.name}_${f.size}_${f.lastModified}` !== id));
  }, []);

  const handleConfirmAppend = useCallback((): void => {
    if (appendSheetFiles.length === 0) return;
    const batchId = appendBatch(appendSheetFiles);
    if (batchId) {
      setAppendedBatches((prev) => ({ ...prev, [batchId]: appendSheetFiles }));
      trackEvent(TRACK_EVENT_NAMES.SmartMenuAppendFiles, { files_count: appendSheetFiles.length });
    }
    setAppendSheetFiles([]);
    setAppendSheetOpen(false);
  }, [appendBatch, appendSheetFiles, trackEvent]);

  // Descarta un batch aditivo fallido: remueve su item del hook y libera el
  // presupuesto de archivos del ledger. El review existente nunca se toca.
  const handleDiscardAppend = useCallback(
    (batchId: string): void => {
      discardBatch(batchId);
      setAppendedBatches((prev) => {
        const next = { ...prev };
        delete next[batchId];
        return next;
      });
    },
    [discardBatch],
  );

  // Renders por phase ---------------------------------------------------------

  if (phase === 'upload') {
    const canProcess = selectedFiles.length >= 1;
    return (
      <div data-testid="smart-menu-onboarding" data-phase="upload" style={containerStyle}>
        {pendingJob ? (
          <RecoveryBanner pending={pendingJob} onRecover={handleRecoverPending} onDiscard={handleDiscardPending} />
        ) : null}
        <MultiFileUploadZone
          onFilesAdded={onFilesAdded}
          currentPdfCount={counts.pdf}
          currentImageCount={counts.image}
          currentTotalMB={counts.totalMB}
          maxPdfs={maxPdfs}
          maxImages={maxImages}
          maxFileMB={maxFileMB}
          maxTotalMB={maxTotalMB}
        />
        {uploadDisplayItems.length > 0 ? (
          <FileQueueGrid items={uploadDisplayItems} onRemove={removeSelectedFile} onRetry={() => undefined} />
        ) : null}
        <div style={ctaWrapperStyle}>
          <Button
            data-testid="smart-menu-onboarding-cta-process"
            variant="primary"
            size="lg"
            disabled={!canProcess}
            onClick={handleStartProcessing}
          >
            Procesar ahora
          </Button>
        </div>
        {skippable ? <SkipFooter onSkip={handleSkip} /> : null}
      </div>
    );
  }

  if (phase === 'processing') {
    // Single-call: el batch tiene 0 o 1 entrada en `items`. Para que la UI siga
    // mostrando un grid con todos los archivos del batch (UX consistente con el
    // flow anterior), espejamos el status del batch al display set de archivos.
    const batchItem = items[0];
    const batchStatus = batchItem?.status ?? 'uploading';
    const mirroredItems: UploadQueueItem[] = selectedFiles.map((file, idx) => ({
      id: `proc_${idx}_${file.name}`,
      file,
      kind: file.type === ACCEPTED_PDF_MIME ? 'pdf' : 'image',
      sizeMB: Math.round((file.size / (1024 * 1024)) * 100) / 100,
      // Todos los archivos comparten el status del batch (single-call: o todos
      // OK, o todos fallaron, o todos en proceso). `remove`/`retry` quedan
      // como callbacks del hook (no-op por id, igualmente afectan al batch).
      status: batchStatus,
      attempt: batchItem?.attempt ?? 0,
      error: batchItem?.error,
      // Batches retomados tras reload: el File es un stub vacío y el preview
      // sale de la download URL de Storage persistida.
      previewUrl: resumedPreviews[file.name],
    }));
    const batchErrorMessage =
      batchStatus === 'failed' && batchItem?.error
        ? typeof batchItem.error === 'string'
          ? batchItem.error
          : batchItem.error.message
        : null;
    return (
      <div data-testid="smart-menu-onboarding" data-phase="processing" style={containerStyle}>
        <ProcessingProgress
          /* Single-call: pasamos conteo de ARCHIVOS reales, no de batches.
             totalProgress.total siempre es 0 o 1 (cardinalidad del batch); el
             usuario espera ver N archivos (el conteo real que subió). El
             componente decide el copy correcto a partir de items[].status. */
          done={batchStatus === 'completed' ? selectedFiles.length : 0}
          total={selectedFiles.length}
          failed={batchStatus === 'failed' ? selectedFiles.length : 0}
          items={mirroredItems}
        />
        {batchErrorMessage ? (
          <div
            data-testid="smart-menu-onboarding-batch-error"
            role="alert"
            style={{
              padding: '12px 16px',
              background: '#FEF2F2',
              border: `1px solid ${V4.danger}`,
              borderRadius: 8,
              color: V4.danger,
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            <strong style={{ display: 'block', marginBottom: 4 }}>No se pudo procesar el menú</strong>
            {batchErrorMessage}
          </div>
        ) : null}
        <FileQueueGrid items={mirroredItems} onRemove={remove} onRetry={retry} />
        <div style={ctaWrapperStyle}>
          {batchStatus === 'failed' ? (
            <Button
              data-testid="smart-menu-onboarding-cta-retry"
              variant="primary"
              size="lg"
              iconLeft={<IconRefresh size={16} aria-hidden="true" />}
              onClick={() => {
                // Resetear el guard de transición antes de reintentar: sin esto,
                // si el retry tiene éxito, el useEffect de línea ~343 ya tiene
                // transitionedRef.current=true del intento fallido y no
                // dispara setPhase('review'), dejando al user atrapado en
                // 'processing' con datos listos. Reset explícito aquí (no en
                // handleRestart, que es 'empezar de cero' y limpia más estado).
                transitionedRef.current = false;
                // El hook decide: re-subir el último batch o, si lo último fue
                // un resume por jobId, re-consultar ese jobId (sin re-subir).
                retry('');
              }}
            >
              Reintentar
            </Button>
          ) : null}
          {renderRestartButton()}
        </div>
      </div>
    );
  }

  if (phase === 'low-extraction') {
    return (
      <div data-testid="smart-menu-onboarding" data-phase="low-extraction" style={containerStyle}>
        <LowExtractionPrompt
          itemsExtracted={analysis.products.length}
          onContinue={() => setPhase('review')}
          onAddManually={handleSkip}
        />
        <div style={ctaWrapperStyle}>{renderRestartButton()}</div>
      </div>
    );
  }

  if (phase === 'review') {
    // Conteos SIEMPRE como intersección con el análisis vigente (no .size del
    // set): un id stale en la selección jamás debe inflar lo que se promete
    // publicar — y handlePersist publica exactamente esta misma intersección.
    const includedCount = viewAnalysis.products.filter((p) => selectedProductIds.has(p.id)).length;
    const includedCategories = viewAnalysis.categories.filter((c) => selectedCategoryIds.has(c.id)).length;
    const canPublish = includedCount > 0;
    // Estado de los batches aditivos lanzados desde esta pantalla.
    const appendedItems = items.filter((it) => appendedBatches[it.id]);
    const appendsInFlight = appendedItems.filter((it) => it.status === 'uploading' || it.status === 'analyzing');
    const failedAppends = appendedItems.filter((it) => it.status === 'failed');
    const appendInFlightFiles = appendsInFlight.reduce((acc, it) => acc + (appendedBatches[it.id]?.length ?? 0), 0);
    return (
      <div data-testid="smart-menu-onboarding" data-phase="review" style={containerStyle}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            flexWrap: 'wrap',
          }}
        >
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: V4.primary }}>Revisa tu menú</h3>
          {renderRestartButton()}
        </div>
        {/* Toolbar de review: agregar más archivos sin salir de la curaduría (spec §6.1). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Button
            data-testid="smart-menu-onboarding-cta-append"
            variant="outline"
            iconLeft={<IconPlus size={16} aria-hidden="true" />}
            disabled={appendsInFlight.length > 0}
            onClick={() => setAppendSheetOpen(true)}
          >
            Agregar fotos o PDF
          </Button>
        </div>
        <AppendBanners
          inFlightCount={appendsInFlight.length}
          inFlightFiles={appendInFlightFiles}
          failedAppends={failedAppends}
          onRetry={retry}
          onDiscard={handleDiscardAppend}
        />
        {viewAnalysis.duplicateGroups.length > 0 ? (
          <DuplicateBanner
            duplicateGroups={viewAnalysis.duplicateGroups.map((g, idx) => ({
              ...g,
              decision: (duplicateDecisions[idx] ?? g.decision) as DuplicateGroup['decision'],
            }))}
            categories={viewAnalysis.categories}
            products={viewAnalysis.products}
            extras={viewAnalysis.extras}
            preDedupeCategoriesById={viewAnalysis.preDedupeCategoriesById}
            preDedupeProductsById={viewAnalysis.preDedupeProductsById}
            preDedupeExtrasById={viewAnalysis.preDedupeExtrasById}
            onDecide={onDecideDuplicate}
          />
        ) : null}
        <ConsolidatedReview
          analysis={viewAnalysis}
          selectedProductIds={selectedProductIds}
          selectedCategoryIds={selectedCategoryIds}
          selectedExtraIds={selectedExtraIds}
          selectedSectionIds={selectedSectionIds}
          onToggleProduct={onToggleProduct}
          onToggleCategory={onToggleCategory}
          onToggleExtra={onToggleExtra}
          onToggleSection={onToggleSection}
          onToggleAll={onToggleAll}
          onEditProduct={onEditProduct}
          onEditCategory={onEditCategory}
          onEditExtra={onEditExtra}
          onEditSection={onEditSection}
          onReassignCategory={onReassignCategory}
          onCreateCategory={onCreateCategory}
          existingCategoryLinks={existingCategoryLinks}
          existingCategoriesById={existingCategoriesById}
          productDuplicateHints={productDuplicateHints}
          onToggleCategoryLink={handleToggleCategoryLink}
        />
        {skippable ? (
          <SkipFooter onSkip={handleSkip} label="Guardar y terminar después" hint="Guardamos tu avance por 24 horas" />
        ) : null}
        {/* Fallo parcial al publicar (spec §6.4): banner persistente, no toast. */}
        {publishError ? (
          <PublishErrorBanner
            error={publishError}
            onRetry={() => {
              handlePersist().catch(() => undefined);
            }}
            onRestart={handleRestart}
          />
        ) : null}
        {/* Barra sticky de publicación: el ÚNICO primario de la pantalla. */}
        <div
          data-testid="smart-menu-onboarding-publish-bar"
          style={{
            position: 'sticky',
            bottom: 0,
            background: V4.white,
            borderTop: `1px solid ${V4.grayLight}`,
            padding: '12px 0 8px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            zIndex: 10,
          }}
        >
          <Button
            data-testid="smart-menu-onboarding-cta-publish"
            variant="primary"
            size="lg"
            fullWidth
            disabled={!canPublish}
            onClick={() => {
              handlePersist().catch(() => undefined);
            }}
          >
            {`Publicar mi menú${includedCount > 0 ? ` (${includedCount})` : ''}`}
          </Button>
          <span style={{ fontSize: 12, color: V4.grayUltraDark }}>
            {canPublish
              ? `Se crearán ${includedCount} producto${includedCount === 1 ? '' : 's'} en ${includedCategories} categoría${includedCategories === 1 ? '' : 's'}`
              : 'Selecciona al menos un producto para publicar'}
          </span>
        </div>
        {/* Bottom sheet para agregar archivos adicionales (límites RESTANTES). */}
        {appendSheetOpen ? (
          <AppendSheet
            usage={appendUsage}
            displayItems={appendSheetDisplayItems}
            fileCount={appendSheetFiles.length}
            maxPdfs={maxPdfs}
            maxImages={maxImages}
            maxFileMB={maxFileMB}
            maxTotalMB={maxTotalMB}
            onFilesAdded={onAppendFilesAdded}
            onRemoveFile={removeAppendSheetFile}
            onClose={() => setAppendSheetOpen(false)}
            onCancel={() => {
              setAppendSheetFiles([]);
              setAppendSheetOpen(false);
            }}
            onConfirm={handleConfirmAppend}
          />
        ) : null}
      </div>
    );
  }

  if (phase === 'persisting') {
    const keyframes =
      '@keyframes sm-onboarding-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
    return (
      <div data-testid="smart-menu-onboarding" data-phase="persisting" style={containerStyle}>
        <style>{keyframes}</style>
        <div style={spinnerOverlayStyle}>
          <div style={spinnerCircleStyle} aria-hidden="true" />
          <span style={{ color: V4.primary, fontSize: 15, fontWeight: 600 }}>Creando tu menú en línea…</span>
        </div>
      </div>
    );
  }

  // phase === 'done'
  return (
    <div data-testid="smart-menu-onboarding" data-phase="done" style={containerStyle}>
      <div style={doneCardStyle}>
        <span style={{ fontSize: 28 }} aria-hidden="true">
          ✓
        </span>
        <span style={{ fontSize: 16, fontWeight: 600 }}>
          Listo. Categorías: {persistedCounts.categories}
          {persistedCounts.categoriesLinked > 0
            ? ` (+${persistedCounts.categoriesLinked} reutilizada${persistedCounts.categoriesLinked === 1 ? '' : 's'})`
            : ''}
          , Productos: {persistedCounts.products}, Secciones: {persistedCounts.sections}, Extras:{' '}
          {persistedCounts.extras}
        </span>
      </div>
      <div style={ctaWrapperStyle}>{renderRestartButton()}</div>
    </div>
  );
};
